import ViewAdjustmentHistory from 'features/Project/components/ViewAdjustmentHistory';
import './style.scss';

import Loading from 'components/Loading';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { Tooltip } from 'primereact/tooltip';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { formatDatetime, toCamelCaseString } from 'utils/func';
import { useGetAdjustmentsById, useGetProjectById } from 'utils/hooks/projects';
import { useGetAllAdjustmentComments } from 'utils/hooks/adjustmentComments';
import { ADJUSTMENT_COMMENTS, REQUEST_ADJUSMENT_STATUSES } from 'constant';
import { Tag } from 'primereact/tag';
import ConfirmDeleteAdjustment from 'features/Project/components/ConfirmDeleteAdjustment';
import { useMutation } from 'react-query';
import { deleteFinancialAdjustment } from 'apis/financialAdjustment.api';
import { deleteProjectDeadlineExtensions } from 'apis/projectDeadlineExtensions.api';
import { deletePersonnelAdjustment } from 'apis/personnelAdjustment.api';

export default function AdjustmentViewList() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const createAdjustmentRef = useRef();
  const deleteAdjustmentRef = useRef();

  const [isViewAdjustmentHistory, setIsViewAdjustmentHistory] = useState(false);
  const [adjustmentCommentsParams, setAdjustmentCommentsParams] = useState({
    adjustmentName: '',
    adjustmentId: '',
  });

  const { data: projectData, isLoading: isLoadingProjectData } = useGetProjectById(projectId, {});
  const projectProposal = useMemo(() => projectData?.projectProposal ?? {}, [projectData]);

  const {
    data: adjustmentData,
    isLoading: isLoadingAdjustmentData,
    refetch,
  } = useGetAdjustmentsById(projectId, {});

  const {
    data: adjustmentComments,
    isLoading: isLoadingAdjustmentComments,
    refetch: refetchAdjustmentComments,
  } = useGetAllAdjustmentComments(adjustmentCommentsParams, {
    enabled: isViewAdjustmentHistory,
  });

  const { mutate: mutateDeleteFinancialAdjustment } = useMutation(deleteFinancialAdjustment);
  const { mutate: mutateDeleteProjectDeadlineExtension } = useMutation(
    deleteProjectDeadlineExtensions
  );
  const { mutate: mutateDeletePersonnelAdjustment } = useMutation(deletePersonnelAdjustment);

  const isCreateNewFinancialAdjustment = useMemo(() => {
    const financialAdjustments =
      adjustmentData?.filter((item) => item.type === ADJUSTMENT_COMMENTS.FINANCIAL_ADJUSTMENT) ??
      [];
    if (financialAdjustments?.length === 0) return true;

    return !financialAdjustments.some(
      (item) =>
        item.status === REQUEST_ADJUSMENT_STATUSES.NEW ||
        item.status === REQUEST_ADJUSMENT_STATUSES.SUBMITTED
    );
  }, [adjustmentData]);

  const isCreateNewProjectDeadlineExtension = useMemo(() => {
    const projectDeadlineExtensions =
      adjustmentData?.filter(
        (item) => item.type === ADJUSTMENT_COMMENTS.PROJECT_DEADLINE_EXTENSION
      ) ?? [];
    if (projectDeadlineExtensions?.length === 0) return true;

    return !projectDeadlineExtensions.some(
      (item) =>
        item.status === REQUEST_ADJUSMENT_STATUSES.NEW ||
        item.status === REQUEST_ADJUSMENT_STATUSES.SUBMITTED
    );
  }, [adjustmentData]);

  const isCreateNewPersonnelAdjustment = useMemo(() => {
    const personnelAdjustments =
      adjustmentData?.filter((item) => item.type === ADJUSTMENT_COMMENTS.PERSONNEL_ADJUSTMENT) ??
      [];
    if (personnelAdjustments?.length === 0) return true;

    return !personnelAdjustments.some(
      (item) =>
        item.status === REQUEST_ADJUSMENT_STATUSES.NEW ||
        item.status === REQUEST_ADJUSMENT_STATUSES.SUBMITTED
    );
  }, [adjustmentData]);

  const mappingUrlAdjustment = {
    FinancialAdjustment: 'financial-adjustment',
    ProjectDeadlineExtension: 'project-deadline-extensions',
    PersonnelAdjustment: 'personnel-adjustment',
  };

  const formatType = (value) => t(`projectAdjustment.type.${value.type}`);

  const formatDate = (time) => {
    const datetime = formatDatetime(time, 'DD/MM/YYYY HH:mm:ss');
    return <span className="text-color-secondary">{datetime}</span>;
  };

  const formatStatus = (value) => {
    const statusColorMapping = {
      update: 'var(--orange-500)',
      submitted: 'var(--blue-500)',
      approved: 'var(--cyan-500)',
      rejected: 'var(--red-500)',
      new: 'var(--green-500)',
    };

    return (
      <Tag
        style={{ background: statusColorMapping[toCamelCaseString(value.status)] }}
        value={t(`projectAdjustment.status.${toCamelCaseString(value.status)}`)}
        rounded
      />
    );
  };

  const formatActions = (value) => {
    let postFix = '';
    if (value.type === ADJUSTMENT_COMMENTS.PERSONNEL_ADJUSTMENT) {
      postFix = `?projectProposalId=${projectProposal?.id}&projectId=${projectId}`;
    }

    return (
      <div className="flex flex-wrap justify-content-center gap-1">
        {(value?.status === REQUEST_ADJUSMENT_STATUSES.NEW ||
          (value?.status === REQUEST_ADJUSMENT_STATUSES.SUBMITTED && value?.editable)) && (
          <Link
            to={{
              pathname: `/project/${projectId}/${mappingUrlAdjustment[value.type]}/edit/${
                value.id
              }`,
              search: postFix,
            }}
            state={value?.editable && { isRequestedEdit: true }}
          >
            <Button
              icon="pi pi-pencil"
              tooltip={t('projectAdjustment.viewList.editAdjustment')}
              tooltipOptions={{ position: 'left', at: 'left top' }}
              severity="warning"
              style={{ width: '42px', height: '42px' }}
            />
          </Link>
        )}
        <Link
          to={`/project/${projectId}/${mappingUrlAdjustment[value.type]}/detail/${
            value.id
          }${postFix}`}
        >
          <Button
            icon="pi pi-eye"
            tooltip={t('projectAdjustment.viewList.viewAdjustment')}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            severity="info"
            style={{ width: '42px', height: '42px' }}
          />
        </Link>
        <div className="button-adjustment-comment-container">
          <Button
            className="button-adjustment-comment"
            icon="pi pi-history"
            tooltip={t('projectAdjustment.viewList.viewAdjustmentHistory')}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            severity="primary"
            style={{ width: '42px', height: '42px' }}
            onClick={() => {
              setAdjustmentCommentsParams({
                adjustmentName: value?.type,
                adjustmentId: value?.id,
              });
              setIsViewAdjustmentHistory(true);
            }}
          />
          {value?.commentCount > 0 && (
            <>
              <span className="adjustment-comment-count">{value?.commentCount}</span>
              <Tooltip
                target=".adjustment-comment-count"
                mouseTrack
                mouseTrackLeft={10}
                content={t('projectAdjustment.viewList.commentCountUnanswer')}
              />
            </>
          )}
        </div>
        {value?.status === REQUEST_ADJUSMENT_STATUSES.NEW && (
          <Button
            icon="pi pi-trash"
            tooltip={t('projectAdjustment.viewList.deleteAdjustment')}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            severity="danger"
            style={{ width: '42px', height: '42px' }}
            onClick={() => {
              let mutate;
              if (value.type === ADJUSTMENT_COMMENTS.FINANCIAL_ADJUSTMENT) {
                mutate = mutateDeleteFinancialAdjustment;
              } else if (value.type === ADJUSTMENT_COMMENTS.PROJECT_DEADLINE_EXTENSION) {
                mutate = mutateDeleteProjectDeadlineExtension;
              } else if (value.type === ADJUSTMENT_COMMENTS.PERSONNEL_ADJUSTMENT) {
                mutate = mutateDeletePersonnelAdjustment;
              }
              deleteAdjustmentRef.current.open({
                mode: 'delete',
                mutate,
                value,
              });
            }}
          />
        )}
      </div>
    );
  };

  const items = [];

  if (isCreateNewFinancialAdjustment) {
    items.push({
      label: t('projectAdjustment.financialAdjustments'),
      icon: 'pi pi-dollar',
      url: `/project/${projectId}/financial-adjustment/create`,
    });
  }

  if (isCreateNewProjectDeadlineExtension) {
    items.push({
      label: t('projectAdjustment.projectDeadlineExtensions'),
      icon: 'pi pi-clock',
      url: `/project/${projectId}/project-deadline-extensions/create`,
    });
  }

  if (isCreateNewPersonnelAdjustment) {
    items.push({
      label: t('projectAdjustment.personnelAdjustments'),
      icon: 'pi pi-user-edit',
      url: `/project/${projectId}/personnel-adjustment/create?projectProposalId=${projectProposal?.id}&projectId=${projectId}`,
    });
  }

  return (
    <>
      {(isLoadingProjectData || isLoadingAdjustmentData) && <Loading />}
      <div className="p-5 text-lg border">
        {items.length > 0 && (
          <div className="flex justify-content-end align-items-end mb-4 pb-2">
            <Button
              className="bg-cyan-600 border-cyan-600"
              label={t('projectAdjustment.createAdjustment')}
              icon="pi pi-plus"
              onClick={(event) => createAdjustmentRef.current.toggle(event)}
              aria-controls="popup_menu_right"
              aria-haspopup
            />
            <Menu
              model={items}
              popup
              ref={createAdjustmentRef}
              id="popup_menu_right"
              popupAlignment="right"
              style={{ width: '16.5rem' }}
            />
          </div>
        )}

        <div className="mb-4">
          {/* project info */}
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.vietnameseName')}:
            </div>
            <div className="flex-1">{projectProposal?.vietnameseName}</div>
          </div>
          <div className="flex mb-1">
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
            </div>
            <div className="flex-1">{projectProposal?.englishName}</div>
          </div>
        </div>

        {/* adjustments list */}
        <DataTable
          value={adjustmentData}
          className="p-datatable-gridlines header-table"
          showGridlines
          dataKey="id"
          filterDisplay="menu"
          scrollable
          loading={isLoadingAdjustmentData}
          emptyMessage={t('projectAdjustment.emptyMessage')}
        >
          <Column
            header={t('proposal.history.no')}
            frozen
            alignFrozen="left"
            body={(rowData, { rowIndex }) => rowIndex + 1}
            style={{ width: '3rem', textAlign: 'center' }}
          />
          <Column
            field="requestedBy"
            // body={formatVietnameseName}
            header={t('projectAdjustment.requestedBy')}
            style={{
              minWidth: '10rem',
              maxWidth: '16rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="requestedAt"
            body={(rowData) => formatDate(rowData?.requestedAt)}
            header={t('projectAdjustment.requestedAt')}
            style={{
              minWidth: '6rem',
              maxWidth: '7rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="reviewedBy"
            // body={formatVietnameseName}
            header={t('projectAdjustment.reviewedBy')}
            style={{
              minWidth: '10rem',
              maxWidth: '16rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="reviewedAt"
            body={(rowData) => formatDate(rowData?.reviewedAt)}
            header={t('projectAdjustment.reviewedAt')}
            style={{
              minWidth: '6rem',
              maxWidth: '7rem',
              textAlign: 'center',
            }}
          />

          <Column
            field="type"
            body={formatType}
            header={t('projectAdjustment.type.label')}
            style={{
              minWidth: '8rem',
              maxWidth: '10rem',
              textAlign: 'left',
            }}
          />

          <Column
            field="rejectionReason"
            header={t('projectAdjustment.reasonReject')}
            style={{
              minWidth: '12rem',
              maxWidth: '16rem',
              textAlign: 'left',
            }}
          />

          <Column
            header={t('projectAdjustment.status.label')}
            body={formatStatus}
            style={{ width: '8rem', textAlign: 'center' }}
          />

          <Column
            header={t('project.action')}
            body={formatActions}
            style={{ width: '14rem', textAlign: 'center' }}
            frozen
            alignFrozen="right"
          />
        </DataTable>
      </div>
      <ViewAdjustmentHistory
        data={adjustmentComments}
        visible={isViewAdjustmentHistory}
        setVisible={setIsViewAdjustmentHistory}
        title={t('projectAdjustment.viewList.viewAdjustmentHistory')}
        isLoading={isLoadingAdjustmentComments}
        refetchAdjustmentComments={refetchAdjustmentComments}
        refetch={refetch}
      />

      <ConfirmDeleteAdjustment ref={deleteAdjustmentRef} refetch={refetch} />
    </>
  );
}
